import React from "react";
import { Modal, Form, Row, Col } from "react-bootstrap";
import { DatePicker } from "rsuite";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useAddDistributorMutation } from "../../State/Api/AddDistributorApiSlice";

const DistributorSchema = Yup.object().shape({
  gstColumn: Yup.string().oneOf(["yes", "no"]).required("Required"),
  gstin: Yup.string()
    .nullable()
    .when("gstColumn", {
      is: "yes",
      then: (s) =>
        s
          .transform((v) => (v === "" ? null : v?.toUpperCase().trim()))
          .required("GSTIN is required")
          .length(15, "GSTIN must be exactly 15 characters"),
      otherwise: (s) => s.transform(() => null).nullable().notRequired(),
    }),
  districtName: Yup.string().required("District is required"),
  stateName: Yup.string()
    .nullable()
    .when("gstColumn", {
      is: "no",
      then: (s) =>
        s.transform((v) => (v === "" ? null : v)).required("State is required"),
      otherwise: (s) => s.transform(() => null).nullable().notRequired(),
    }),
  distributorName: Yup.string().required("Name is required"),
  mobileNumber: Yup.string()
    .nullable()
    .matches(/^[0-9]{10}$/, { message: "Enter 10 digit mobile", excludeEmptyString: true }),
  email: Yup.string().email("Invalid email").nullable(),
  address: Yup.string().nullable(),
  drugLicense: Yup.string().nullable(),
  openingBalance: Yup.number()
    .nullable()
    .transform((_, v) => (v === "" || v === null ? null : Number(v)))
    .typeError("Enter a number"),
  openingBalanceDate: Yup.mixed().nullable(),
});

function AddDistributorModel({ isOpen, onClose, editData = null }) {
  const isEditMode = !!editData;
  const [addDistributor, { isLoading }] = useAddDistributorMutation();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      gstColumn: editData?.gstColumn || "yes",
      gstin: editData?.gstNo?.toUpperCase() || "",
      districtName: editData?.districtName || "",
      stateName: editData?.stateName || "",
      distributorName: editData?.distributorName || "",
      mobileNumber: editData?.phone || "",
      email: editData?.email || "",
      address: editData?.address || "",
      drugLicense: editData?.drugLic || "",
      openingBalance: editData?.openingBalance || "",
      openingBalanceDate: editData?.openingBalanceDate
        ? new Date(editData.openingBalanceDate)
        : null,
    },
    validationSchema: DistributorSchema,
    onSubmit: async (values, helpers) => {
      const payload = DistributorSchema.cast(values);

      const body = {
        distributorId: isEditMode ? editData.distributorId : 0,
        pharmaId: localStorage.getItem("userUniqueID") || "0",
        distributorName: payload.distributorName,
        gstNum: payload.gstin,
        districtName: payload.districtName,
        state: payload.stateName,
        mobileNum: payload.mobileNumber,
        emailAddress: payload.email,
        addresses: payload.address,
        drugLicense: payload.drugLicense,
        openBalance: payload.openingBalance || 0,
        openDate: payload.openingBalanceDate
          ? new Date(payload.openingBalanceDate).toISOString()
          : null,
        createdBy: "admin",
        updatedBy: "admin",
        isActive: true,
      };

      try {
        await addDistributor(body).unwrap();
        toast.success(isEditMode ? "Distributor updated!" : "Distributor added!");
        helpers.resetForm();
        onClose();
      } catch (err) {
        toast.error(err?.data?.message || "Failed");
      }
    },
  });




  return (
    <>
      <style>{`
        .rs-input-group { border-radius: 8px !important; border: #e5e5ea solid 1px !important; height: 40px; overflow: hidden; background: white; }
        .rs-input-group .rs-input { border-radius: 5px !important; border: none !important; background: none !important; box-shadow: none !important; }
        .rs-input-group .rs-input:focus { outline: none; box-shadow: none; }
        .rs-picker-toggle { border-radius: 8px !important; }
        .rs-input-group-inside .rs-input { border: none !important; font-size: 14px; border-radius: 8px !important; box-shadow: none !important; background: none !important; }
        .radio-label { cursor: pointer; margin-right: 16px; user-select: none; }
        .error-text { color: #dc3545; font-size: 12px; margin-top: 4px; }
      `}</style>

      <Modal show={isOpen} onHide={onClose} size="md" centered backdrop="static">
        <Modal.Header closeButton style={{ borderBottom: "none" }}>
          <Modal.Title
            style={{
              borderBottom: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              fontWeight: "600",
              fontSize: "22px",
            }}
          >
            {isEditMode ? "Update Distributor" : "Add Distributor"}
            <button type="submit" form="distributorForm" className="gradient-button" disabled={isLoading}>
  {isEditMode ? "Update Distributor" : "Save Distributor"}
</button>
          </Modal.Title>
        </Modal.Header>

     <form id="distributorForm" onSubmit={formik.handleSubmit}>
  <Modal.Body>
    {/* Purchase Bill Has */}
    <Row className="mb-3">
      <Col>
        <Form.Group>
          <Form.Label className="label-title">Purchase Bill Has</Form.Label>

          <div>
            <label className="radio-label">
              <input
                type="radio"
                name="gstColumn"
                value="yes"
                checked={formik.values.gstColumn === "yes"}
                onChange={(e) => {
                  formik.setFieldValue("gstColumn", e.target.value);
                  formik.setFieldValue("stateName", null);
                }}
              /> GST Column
            </label>

            <label className="radio-label">
              <input
                type="radio"
                name="gstColumn"
                value="no"
                checked={formik.values.gstColumn === "no"}
                onChange={(e) => {
                  formik.setFieldValue("gstColumn", e.target.value);
                  formik.setFieldValue("gstin", null);
                  formik.setFieldValue("stateName", null);
                }}
              /> No GST Column
            </label>
          </div>
          {formik.touched.gstColumn && formik.errors.gstColumn && (
            <div className="error-text">{formik.errors.gstColumn}</div>
          )}
        </Form.Group>
      </Col>
    </Row>

    {/* GST mode */}
    {formik.values.gstColumn === "yes" && (
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label className="label-title">Distributor GSTIN *</Form.Label>
            <Form.Control
              type="text"
              name="gstin"
              value={formik.values.gstin}
              onChange={formik.handleChange}
              onBlur={(e) => {
                const v = (e.target.value || "").toUpperCase().trim();
                formik.setFieldValue("gstin", v || null);
                if (v && v.length !== 15) toast.error("GSTIN must be exactly 15 characters.");
              }}
              placeholder="Enter GSTIN"
              style={{ textTransform: "uppercase" }}
              maxLength={15}
            />
            {formik.touched.gstin && formik.errors.gstin && (
              <div className="error-text">{formik.errors.gstin}</div>
            )}
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label className="label-title">District Name *</Form.Label>
            <Form.Control
              name="districtName"
              value={formik.values.districtName}
              onChange={formik.handleChange}
              placeholder="Enter District Name"
            />
            {formik.touched.districtName && formik.errors.districtName && (
              <div className="error-text">{formik.errors.districtName}</div>
            )}
          </Form.Group>
        </Col>
      </Row>
    )}

    {/* NO GST mode */}
    {formik.values.gstColumn === "no" && (
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label className="label-title">District Name *</Form.Label>
            <Form.Control
              name="districtName"
              value={formik.values.districtName}
              onChange={formik.handleChange}
              placeholder="Enter District Name"
            />
            {formik.touched.districtName && formik.errors.districtName && (
              <div className="error-text">{formik.errors.districtName}</div>
            )}
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label className="label-title">State Name *</Form.Label>
            <Form.Control
              name="stateName"
              value={formik.values.stateName}
              onChange={formik.handleChange}
              onBlur={(e) => {
                const v = e.target.value?.trim();
                formik.setFieldValue("stateName", v || null);
              }}
              placeholder="Enter State Name"
            />
            {formik.touched.stateName && formik.errors.stateName && (
              <div className="error-text">{formik.errors.stateName}</div>
            )}
          </Form.Group>
        </Col>
      </Row>
    )}

    {/* distributorName */}
    <Row className="mb-3">
      <Col>
        <Form.Group>
          <Form.Label className="label-title">Distributor's Name *</Form.Label>
          <Form.Control
            name="distributorName"
            value={formik.values.distributorName}
            onChange={formik.handleChange}
            placeholder="Enter distributor name"
          />
          {formik.touched.distributorName && formik.errors.distributorName && (
            <div className="error-text">{formik.errors.distributorName}</div>
          )}
        </Form.Group>
      </Col>
    </Row>

    {/* mobile + email */}
    <Row className="mb-3">
      <Col md={6}>
        <Form.Group>
  <Form.Label className="label-title">Mobile Number</Form.Label>
  <Form.Control
    type="text"
    name="mobileNumber"
    maxLength={10}
    value={formik.values.mobileNumber}
    onChange={(e) => {
      // Remove any non-digit characters
      const onlyDigits = e.target.value.replace(/\D/g, "");
      formik.setFieldValue("mobileNumber", onlyDigits);
    }}
    onBlur={formik.handleBlur}
    placeholder="Enter mobile"
  />
  {formik.touched.mobileNumber && formik.errors.mobileNumber && (
    <div className="error-text">{formik.errors.mobileNumber}</div>
  )}
</Form.Group>
      </Col>

      <Col md={6}>
        <Form.Group>
          <Form.Label className="label-title">Email ID</Form.Label>
          <Form.Control
            type="email"
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            placeholder="Enter email ID"
          />
          {formik.touched.email && formik.errors.email && (
            <div className="error-text">{formik.errors.email}</div>
          )}
        </Form.Group>
      </Col>
    </Row>

    {/* address */}
    <Row className="mb-3">
      <Col>
        <Form.Group>
          <Form.Label className="label-title">Distributor's Address</Form.Label>
          <Form.Control
            name="address"
            value={formik.values.address}
            onChange={formik.handleChange}
            placeholder="Enter full address"
          />
        </Form.Group>
      </Col>
    </Row>

    {/* license */}
    <Row className="mb-3">
      <Col>
        <Form.Group>
          <Form.Label className="label-title">Distributor's Drug License</Form.Label>
          <Form.Control
            name="drugLicense"
            value={formik.values.drugLicense}
            onChange={formik.handleChange}
            placeholder="Enter Form 20 & Form 21 here"
          />
        </Form.Group>
      </Col>
    </Row>

    {/* opening balance */}
    {/* <Row className="mb-3">
      <Col md={6}>
        <Form.Group>
          <Form.Label className="label-title">Opening Balance</Form.Label>
          <Form.Control
            type="number"
            name="openingBalance"
            value={formik.values.openingBalance}
            onChange={formik.handleChange}
            placeholder="Enter opening balance"
          />
        </Form.Group>
      </Col>

      <Col md={6}>
        <Form.Group>
          <Form.Label className="label-title">Opening Balance Date</Form.Label>
          <DatePicker
            name="openingBalanceDate"
            value={formik.values.openingBalanceDate || null}
            onChange={(val) => formik.setFieldValue("openingBalanceDate", val)}
            format="dd-MMM-yyyy"
            placeholder="dd-mmm-yyyy"
            oneTap
            placement="topStart"
            style={{ width: "100%", height: "40px" }}
          />
        </Form.Group>
      </Col>
    </Row> */}
  </Modal.Body>
</form>

      </Modal>
    </>
  );
}

export default AddDistributorModel;
